import React from 'react'
import { Box, Typography } from '@mui/material'

interface OnboardingStepperProps {
  currentStep: 1 | 2 | 3
  onStepClick?: (step: number) => void
}

const steps = [
  { label: 'Core Profile' },
  { label: 'Additional Info' },
  { label: 'Completed' },
]

const CIRCLE_SIZE = 24

const ActiveCircle = () => (
  <svg width={CIRCLE_SIZE} height={CIRCLE_SIZE} viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`} fill="none">
    {/* Dashed outer ring — strokeDasharray controls dash length / gap */}
    <circle
      cx={CIRCLE_SIZE / 2}
      cy={CIRCLE_SIZE / 2}
      r={CIRCLE_SIZE / 2 - 1.5}
      stroke="#43D9AD"
      strokeWidth="2"
      strokeDasharray="3 3"
      strokeLinecap="round"
    />
    {/* Inner filled dot */}
    <circle cx={CIRCLE_SIZE / 2} cy={CIRCLE_SIZE / 2} r="6" fill="#43D9AD" />
  </svg>
)

const InactiveCircle = () => (
  <svg width={CIRCLE_SIZE} height={CIRCLE_SIZE} viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`} fill="none">
    <circle
      cx={CIRCLE_SIZE / 2}
      cy={CIRCLE_SIZE / 2}
      r={CIRCLE_SIZE / 2 - 1.5}
      stroke="#ffffff"
      strokeWidth="2"
    />
  </svg>
)

const CompletedCircle = () => (
  <svg width={CIRCLE_SIZE} height={CIRCLE_SIZE} viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`} fill="none">
    <circle
      cx={CIRCLE_SIZE / 2}
      cy={CIRCLE_SIZE / 2}
      r={CIRCLE_SIZE / 2 - 1.5}
      stroke="#43D9AD"
      strokeWidth="2"
      fill="#43D9AD"
    />
    <path
      d="M8 12l3.5 3.5L17 9"
      stroke="#fff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export const OnboardingStepper = ({ currentStep, onStepClick }: OnboardingStepperProps) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', mb: 4 }}>
      {steps.map((step, index) => {
        const stepNumber = index + 1
        const isCompleted = stepNumber < currentStep
        const isActive = stepNumber === currentStep
        const isClickable = isCompleted && !!onStepClick

        return (
          <React.Fragment key={step.label}>
            <Box
              onClick={isClickable ? () => onStepClick?.(stepNumber) : undefined}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', pb: '22px', cursor: isClickable ? 'pointer' : 'default' }}>
              {isActive ? <ActiveCircle /> : isCompleted ? <CompletedCircle /> : <InactiveCircle />}
              <Typography
                variant="body2"
                sx={{
                  position: 'absolute',
                  top: CIRCLE_SIZE + 4,
                  ...(index === 0
                    ? { left: 0 }
                    : index === steps.length - 1
                    ? { right: 0 }
                    : { left: '50%', transform: 'translateX(-50%)' }),
                  color: isActive || isCompleted ? '#43D9AD' : 'rgba(255,255,255,0.4)',
                  fontFamily: 'InterSemiBold',
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                }}>
                {step.label}
              </Typography>
            </Box>

            {index < steps.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: '5px',
                  backgroundColor: isCompleted ? '#43D9AD' : '#ffffff',
                  borderRadius: '999px',
                  mt: `${CIRCLE_SIZE / 2 - 2.5}px`,
                  mx: '4px',
                }}
              />
            )}
          </React.Fragment>
        )
      })}
    </Box>
  )
}
